import React, { useEffect, useState } from 'react';
import { useMatch } from 'react-router-dom';
import { Grid, styled, useTheme } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../state/hooks';
import { fetchAreas } from '../state/features/mapSlice';
import { fetchAreaBalanceDetails } from '../state/features/areaBalanceDetailsSlice';
import { privateRoutes } from '../routes/appRouteList';
import { ApiStatus, DayOfWeek } from '../utils/enums';
import { useFilters } from '../components/Filters/useFilters';
import Hidden from '../components/layout/hidden';
import BasePage from './Layouts/BasePage';
import PagePaper from './Layouts/PagePaper';
import PageHeader from './Layouts/PageHeader';
import AreaBalanceHeader from '../components/Details/Headers/AreaBalanceHeader';
import Volume from '../components/Widgets/Volume';
import DailyAreaBalance from '../components/Widgets/DailyAreaBalance';
import AreaBalance from '../components/Widgets/AreaBalance';
import BalanceByDayOfWeek from '../components/Widgets/BalanceByDayOfWeek';
import AreaMap from '../components/Map/AreaMap';
import BalanceLineChart from '../components/Graphs/BalanceLineChart';

const classesPrefix = 'areaBalance';

const classes = {
    widgetContainer: `${classesPrefix}-widgetContainer`,
    mapContainer: `${classesPrefix}-mapContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`& .${classes.widgetContainer}`]: {
            padding: theme.spacing(2),
        },
        [`& .${classes.mapContainer}`]: {
            height: '100%',
            maxHeight: '450px',
            [theme.breakpoints.down('md')]: {
                maxHeight: '300px',
            },
        },
    };
});

const AreaBalanceDetails = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const match = useMatch(privateRoutes.areaBalance);
    const theme = useTheme();
    const filters = useFilters();

    const areaName = match?.params.name;

    const [selectedBalanceView, setSelectedBalanceView] = useState<DayOfWeek | 'daily'>('daily');

    const areaBalanceDetailsApiStatus = useAppSelector((state) => { return state.areaBalance.areaBalanceDetailsApiStatus; });
    const areaBalanceDetails = useAppSelector((state) => { return state.areaBalance.areaBalanceDetails; });

    const areasApiStatus = useAppSelector((state) => { return state.map.areasApiStatus; });
    const areas = useAppSelector((state) => { return state.map.areas; });

    useEffect(() => {
        if (areaName) {
            dispatch(fetchAreaBalanceDetails({
                service_area_id: areaName,
                filter_criteria: filters,
            }));
        }
    }, [dispatch, areaName, filters]);

    useEffect(() => {
        if (areaName) {
            dispatch(fetchAreas({
                origin: areaName,
                destination: areaName,
            }));
        }
    }, [dispatch, areaName]);

    return (
        <BasePage>
            <PagePaper>
                <PageHeader>
                    <AreaBalanceHeader />
                </PageHeader>
                <StyledGrid container>
                    <Grid item xs={12} lg={8} className={classes.widgetContainer}>
                        <Grid container spacing={4}>
                            <Grid item xs={12} sm={6}>
                                <Grid container spacing={1}>
                                    <Grid item xs={12}>
                                        <Volume
                                            loading={areaBalanceDetailsApiStatus === ApiStatus.Loading}
                                            orderVolume={
                                                areaBalanceDetails.inbound_freight_order_total_count +
                                                areaBalanceDetails.outbound_freight_order_total_count +
                                                areaBalanceDetails.service_area_freight_order_total_count
                                            }
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <DailyAreaBalance />
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <AreaBalance
                                    title='Balance'
                                    apiStatus={areaBalanceDetailsApiStatus}
                                    inboundOrders={areaBalanceDetails.inbound_freight_order_total_count}
                                    outboundOrders={areaBalanceDetails.outbound_freight_order_total_count}
                                    intraAreaOrders={areaBalanceDetails.service_area_freight_order_total_count}
                                    isDailyBalance={false}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <BalanceByDayOfWeek
                                    selectedBalanceView={selectedBalanceView}
                                    handleDayOfWeekClick={(dayOfWeek): void => {
                                        setSelectedBalanceView(dayOfWeek);
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Hidden direction='down' breakpoint='md'>
                        <Grid item xs={12} lg={4} className={classes.mapContainer}>
                            <AreaMap
                                isFetchingData={
                                    areasApiStatus === ApiStatus.Idle || areasApiStatus === ApiStatus.Loading ||
                                    areaBalanceDetailsApiStatus === ApiStatus.Idle || areaBalanceDetailsApiStatus === ApiStatus.Loading
                                }
                                areaName={areaBalanceDetails.service_area_name || areaName || ''}
                                areas={areas}
                                theme={theme}
                            />
                        </Grid>
                    </Hidden>

                    <Grid item xs={12} className={classes.widgetContainer}>
                        <BalanceLineChart
                            selectedBalanceView={selectedBalanceView}
                            handleBackClick={(): void => {
                                setSelectedBalanceView('daily');
                            }}
                        />
                    </Grid>
                </StyledGrid>
            </PagePaper>
        </BasePage>
    );
};

export default AreaBalanceDetails;
